"use client";

import { useEffect, useState } from "react";
import Button from "../ui/Button";
import Link from "next/link";
import Image from "next/image";
import ChevronRightIcon from "../icons/ChevronRightIcon";
import { fetchPublishedBlogCards, type PublishedBlogCard } from "@/lib/cms-blogs";
import { cmsImageUnoptimized, resolveCmsImageSrc } from "@/lib/cms-image-url";

const HOMEPAGE_BLOG_COUNT = 6;

function cardImage(card: PublishedBlogCard): string | null {
  return resolveCmsImageSrc(card.featuredImage, "") || null;
}

export default function BlogsSection() {
  const [articles, setArticles] = useState<PublishedBlogCard[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const rows = await fetchPublishedBlogCards(HOMEPAGE_BLOG_COUNT);
        if (!cancelled) setArticles(rows.slice(0, HOMEPAGE_BLOG_COUNT));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="w-full px-4 sm:px-6 lg:px-8 pb-12" aria-label="Latest Cannabis News & Articles">
      <div className="flex flex-col w-full items-start gap-6">
        <header className="flex items-center justify-between w-full">
          <h2 className="text-h3-mobile sm:text-h3-desktop font-bold text-text-primary">
            Latest Cannabis News & Articles
          </h2>
          <Button asChild variant="primary" size="lg" className="w-fit rounded-xl max-sm:hidden">
            <Link href="/blog">
              View all articles
            </Link>
          </Button>
        </header>

        {loading ? (
          <div className="w-full py-12 text-center text-body-16 text-text-secondary">Loading articles…</div>
        ) : articles.length === 0 ? (
          <div className="w-full rounded-2xl border border-neutral-10 bg-neutral-5 px-6 py-10 text-center">
            <p className="text-body-16 text-text-secondary">New stories are on the way. Check back soon.</p>
            <Button asChild variant="primary" size="md" className="w-fit rounded-xl">
              <Link href="/blog">
                Blog
              </Link>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 w-full">
            {articles.map((article) => {
              const href = `/blog/${article.slug}`;
              const src = cardImage(article);
              return (
                <div key={article.id} className="flex flex-col gap-4 bg-transparent">
                  <div className="relative w-full h-[243px] rounded-2xl overflow-hidden border border-solid border-neutral-10">
                    {src ? (
                      <Image
                        src={src}
                        alt=""
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        unoptimized={cmsImageUnoptimized(src)}
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-blue-80" />
                    )}
                    <div className="pointer-events-none absolute inset-0 rounded-2xl bg-[linear-gradient(180deg,rgba(23,22,90,0.5)_0%,rgba(52,59,145,0.2)_100%)] opacity-70" />
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <Link href={href} className="flex-1 group">
                      <h3 className="text-body-16 sm:text-body-16-desktop font-bold text-text-primary leading-snug group- transition-colors">
                        {article.title}
                      </h3>
                    </Link>
                    <Button asChild variant="primary" size="icon-md">
                      <Link href={href} aria-label={`Read: ${article.title}`}>
                        <ChevronRightIcon size={16} color="#FFFFFF" className="w-4 h-4 text-constant-white" />
                      </Link>
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && articles.length > 0 ? (
          <Button asChild variant="primary" size="lg" className="w-full rounded-xl sm:hidden mt-6">
            <Link href="/blog">
              View all articles
            </Link>
          </Button>
        ) : null}
      </div>
    </section>
  );
}
